import { signal, effect } from '@preact/signals-react';

// Default values
export const defaultValues = {
  threshold: -50,
  interval: 100,
  timeout: 5000,
  disableTimeout: false,
  microphoneDeviceId: 'default',
  // VAD settings
  vadThreshold: 0.7,
  silenceThreshold: 800,
  smartTurnEnabled: false
};

// LocalStorage key
const STORAGE_KEY = 'audioStore';

// Load initial values from localStorage or use defaults
const loadInitialState = () => {
  try {
    const savedState = localStorage.getItem(STORAGE_KEY);
    if (savedState) {
      return { ...defaultValues, ...JSON.parse(savedState) };
    }
  } catch (error) {
    console.error('Failed to load audio settings from localStorage:', error);
  }
  return defaultValues;
};

const initialState = loadInitialState();

// Define signals with the loaded values
export const threshold = signal<number>(initialState.threshold);
export const interval = signal<number>(initialState.interval);
export const timeout = signal<number>(initialState.timeout);
export const disableTimeout = signal<boolean>(initialState.disableTimeout);
export const microphoneDeviceId = signal<string>(initialState.microphoneDeviceId);

// New VAD signals
export const vadThreshold = signal<number>(initialState.vadThreshold);
export const silenceThreshold = signal<number>(initialState.silenceThreshold);
export const smartTurnEnabled = signal<boolean>(initialState.smartTurnEnabled);

/**
 * Transient signal to queue a language switch while audio recognition is active.
 * Holds the pending new source/target language codes until recognition ends or silence threshold.
 */
export const pendingLanguageSwitch = signal<{
  newSourceCode: string;
  newTargetCode: string;
  newSourceTwoLetter: string;
} | null>(null);


// Setup automatic persistence to localStorage whenever any signal changes
effect(() => {
  const currentState = {
    threshold: threshold.value,
    interval: interval.value,
    timeout: timeout.value,
    disableTimeout: disableTimeout.value,
    microphoneDeviceId: microphoneDeviceId.value,
  };
  
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(currentState));
  } catch (error) {
    console.error('Failed to save audio settings to localStorage:', error);
  }
});

// Reset function to restore defaults
export function reset() {
  threshold.value = defaultValues.threshold;
  interval.value = defaultValues.interval;
  timeout.value = defaultValues.timeout;
  disableTimeout.value = defaultValues.disableTimeout;
  microphoneDeviceId.value = defaultValues.microphoneDeviceId;

}

export function resetVad() {

  // Reset new VAD settings
  vadThreshold.value = defaultValues.vadThreshold;
  silenceThreshold.value = defaultValues.silenceThreshold;
  smartTurnEnabled.value = defaultValues.smartTurnEnabled;
}
